import React, { useState } from 'react';
import styled from 'styled-components';

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(4px);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 10000;
  animation: fadeIn 0.3s ease;

  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
`;

const Modal = styled.div`
  background: var(--bg-primary);
  border-radius: 16px;
  padding: 2rem;
  max-width: 600px;
  width: 90%;
  max-height: 80vh;
  overflow-y: auto;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
  animation: slideIn 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;

  @keyframes slideIn {
    from {
      opacity: 0;
      transform: translateY(-20px) scale(0.95);
    }
    to {
      opacity: 1;
      transform: translateY(0) scale(1);
    }
  }
`;

const Title = styled.h2`
  font-size: 1.5rem;
  font-weight: 900;
  color: var(--accent-orange);
  margin-bottom: 1.5rem;
  font-family: 'Poppins', sans-serif;
`;

const Section = styled.div`
  margin-bottom: 2rem;
`;

const SectionTitle = styled.h3`
  font-size: 1.1rem;
  font-weight: 700;
  color: var(--text-gray);
  margin-bottom: 1rem;
  font-family: 'Poppins', sans-serif;
`;

const CheckboxList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const CheckboxItem = styled.label`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 8px;
  transition: background 0.2s;

  &:hover {
    background: var(--bg-sidebar);
  }
`;

const Checkbox = styled.input`
  width: 18px;
  height: 18px;
  cursor: pointer;
  accent-color: var(--accent-orange);
`;

const CheckboxLabel = styled.span`
  font-size: 0.95rem;
  font-weight: 600;
  color: var(--text-gray);
  font-family: 'Poppins', sans-serif;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
  margin-top: 2rem;
`;

const Button = styled.button<{ $variant?: 'secondary' }>`
  padding: 0.6rem 1.25rem;
  background: ${props => props.$variant === 'secondary' ? 'transparent' : 'var(--accent-orange)'};
  color: ${props => props.$variant === 'secondary' ? 'var(--text-gray)' : 'white'};
  border: ${props => props.$variant === 'secondary' ? '2px solid var(--card-border)' : 'none'};
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 700;
  cursor: pointer;
  font-family: 'Poppins', sans-serif;
  transition: all 0.2s;
  white-space: nowrap;

  &:hover {
    background: ${props => props.$variant === 'secondary' ? 'var(--bg-sidebar)' : 'var(--accent-orange-dark)'};
    transform: translateY(-2px);
    box-shadow: ${props => props.$variant === 'secondary' ? 'none' : '0 4px 12px rgba(255, 150, 0, 0.3)'};
  }
`;

export interface DashboardSettingsProps {
  isOpen: boolean;
  onClose: () => void;
  topMetrics: string[];
  bottomSections: string[];
  onSave: (newTopMetrics: string[], newBottomSections: string[]) => void;
}

const availableMetrics = [
  { value: 'totalSessions', label: 'Number of Training Sessions' },
  { value: 'skiJumpingSessions', label: 'Number of Ski Jumping Training Sessions' },
  { value: 'groundTrainingSessions', label: 'Number of Ground Training Sessions' },
  { value: 'totalJumps', label: 'Total Jumps' },
  { value: 'averageDistance', label: 'Average Distance' },
  { value: 'bestDistance', label: 'Best Distance' },
  { value: 'totalWins', label: 'Total Wins as a Coach' },
  { value: 'competitionSessions', label: 'Number of Competition Sessions' },
];

const availableSections = [
  { value: 'topPerformersBestDistance', label: 'Top Performers - Best Distance' },
  { value: 'topPerformersAverageDistance', label: 'Top Performers - Average Distance' },
  { value: 'plannedTrainingSessions', label: 'Planned Training Sessions' },
  { value: 'plannedCompetitionSessions', label: 'Planned Competition Sessions' },
  { value: 'jumpsPerAthlete', label: 'Number of Jumps per Athlete' },
  { value: 'weightOfAthletes', label: 'Weight of Athletes' },
  { value: 'sessionsPerAthlete', label: 'Number of Training Sessions per Athlete' },
  { value: 'currentForm', label: 'Current Form of Athletes (Last 2 weeks)' },
];

const DashboardSettings: React.FC<DashboardSettingsProps> = ({
  isOpen,
  onClose,
  topMetrics,
  bottomSections,
  onSave,
}) => {
  const [selectedTopMetrics, setSelectedTopMetrics] = useState<string[]>(topMetrics);
  const [selectedBottomSections, setSelectedBottomSections] = useState<string[]>(bottomSections);

  React.useEffect(() => {
    if (isOpen) {
      setSelectedTopMetrics(topMetrics);
      setSelectedBottomSections(bottomSections);
    }
  }, [isOpen, topMetrics, bottomSections]);

  const handleMetricToggle = (value: string) => {
    setSelectedTopMetrics(prev => {
      if (prev.includes(value)) {
        return prev.filter(v => v !== value);
      } else {
        return [...prev, value];
      }
    });
  };

  const handleSectionToggle = (value: string) => {
    setSelectedBottomSections(prev => {
      if (prev.includes(value)) {
        return prev.filter(v => v !== value);
      } else {
        return [...prev, value];
      }
    });
  };

  const handleSave = () => {
    if (selectedTopMetrics.length === 0) {
      alert('Please select at least one top metric');
      return;
    }
    if (selectedBottomSections.length === 0) {
      alert('Please select at least one bottom section');
      return;
    }
    onSave(selectedTopMetrics, selectedBottomSections);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <Overlay onClick={onClose}>
      <Modal onClick={(e) => e.stopPropagation()}>
        <Title>Dashboard Settings</Title>
        
        <Section>
          <SectionTitle>Top Metrics (Select up to 3)</SectionTitle>
          <CheckboxList>
            {availableMetrics.map(metric => (
              <CheckboxItem key={metric.value}>
                <Checkbox
                  type="checkbox"
                  checked={selectedTopMetrics.includes(metric.value)}
                  onChange={() => handleMetricToggle(metric.value)}
                  disabled={!selectedTopMetrics.includes(metric.value) && selectedTopMetrics.length >= 3}
                />
                <CheckboxLabel>{metric.label}</CheckboxLabel>
              </CheckboxItem>
            ))}
          </CheckboxList>
        </Section>

        <Section>
          <SectionTitle>Bottom Sections (Select up to 2)</SectionTitle>
          <CheckboxList>
            {availableSections.map(section => (
              <CheckboxItem key={section.value}>
                <Checkbox
                  type="checkbox"
                  checked={selectedBottomSections.includes(section.value)}
                  onChange={() => handleSectionToggle(section.value)}
                  disabled={!selectedBottomSections.includes(section.value) && selectedBottomSections.length >= 2}
                />
                <CheckboxLabel>{section.label}</CheckboxLabel>
              </CheckboxItem>
            ))}
          </CheckboxList>
        </Section>

        <ButtonGroup>
          <Button $variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleSave}>
            Save
          </Button>
        </ButtonGroup>
      </Modal>
    </Overlay>
  );
};

export default DashboardSettings;
